import {
	createColumnHelper,
	ColumnDef,
} from "@tanstack/react-table";

// Custom components

import { User } from 'api/types';

import { Header } from 'views/admin/simulator/components/datatable';
import { ApiDeleteUser, ApiEnableUser, ApiUpdateUser, ApiCreateUser } from 'api/auth/management/user';
import { useState } from 'react';

import { DynamicFormModal, FormField, valueOption, DynamicModalFormOptions } from 'components/managementModals/DynamicFormModal';

import ManagementTable from './BaseTable';
import { TextValue, CheckValue, ManagementModalProps, EditModal } from './BaseTable';

const helper = createColumnHelper<User>();

export const UserManagementModal = (props: ManagementModalProps) => {

	const { object, options, mode, viewReloader } = props

	const create = mode === 'create'
	const title = create ? 'Crear Usuario' : 'Actualizar Usuario'
	const user: User = object
	const groupOptions = options.filter((o) => o.for === 'groups')[0].options

	const [error, setError] = useState(null)
	const { isOpen, onClose } = props.disclosure

	let permissionOptions = options.filter((o) => o.for === "permissions")[0]?.options
	permissionOptions = permissionOptions ?? []

	let requestBody: { [x: string]: any } = {}
	let modified = false

	const genericHandler = (ev: any) => {
		let value: string = ev.target.value
		let attribute: string = ev.target.name
		if (value.length === 0) { return }
		requestBody[attribute] = value
		modified = true
	}

	const checkHandler = (ev: any) => {
		let value: string = ev.target.checked
		let attribute: string = ev.target.name
		if (value.length === 0) { return }
		requestBody[attribute] = value
		modified = true
	}

	const departmentHandler = (ev: any) => {
		let value: {name: string} = {name: ev.value}
		requestBody['department'] = value
		modified = true
	} 

	const groupHandler = (ev: any[]) => {
		let value: { name: string }[] = ev.map((e) => ({ name: e.value }))
		requestBody['groups'] = value
		modified = true
	}

	const permissionHandler = (ev: any[]) => {
		requestBody['user_permissions'] = ev.map((e) => ({codename: e.value}))
		modified = true
	}

	const formFields: FormField[] = [
		{
			displayableName: 'Usuario',
			valueName: 'username',
			type: 'text',
			readOnly: false,
			required: create,
			defaultValue: user?.username,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Nombre',
			valueName: 'first_name',
			type: 'text',
			required: create,
			defaultValue: user?.first_name,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Apellido',
			valueName: 'last_name',
			type: 'text',
			required: create,
			defaultValue: user?.last_name,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Email',
			valueName: 'email',
			type: 'text',
			required: create,
			defaultValue: user?.email,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Acceso a API',
			valueName: 'api_access',
			type: 'boolean',
			defaultValue: user?.api_access,
			events: [{ on: 'Change', action: checkHandler }]
		},
		{
			displayableName: 'Contraseña',
			valueName: 'password',
			type: 'password',
			required: create,
			defaultValue: null,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Telefono',
			valueName: 'phone_number',
			type: 'text',
			required: false,
			defaultValue: user?.phone_number,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Activo',
			valueName: 'is_active',
			type: 'boolean',
			defaultValue: user?.is_active,
			events: [{ on: 'Change', action: checkHandler }]
		},
		{
			displayableName: 'Departamento',
			valueName: 'department',
			type: 'option',
			required: create,
			defaultValue: user ? {label: user.department.name, value: user.department.name} : null,
			options: groupOptions,
			events: [{ on: 'Change', action: departmentHandler }]
		},
		{
			displayableName: 'Grupos',
			valueName: 'groups',
			type: 'multi-option',
			required: create,
			defaultValue: user?.groups.map((g) => ({label: g.name, value: g.name})),
			options: groupOptions,
			events: [{ on: 'Change', action: groupHandler }]
		},
		{
			displayableName: 'Permisos',
			valueName: 'user_permissions',
			type: 'multi-option',
			options: permissionOptions,
			required: create,
			defaultValue: user?.user_permissions.map((p) => ({label: p.name, value: p.codename})),
			events: [{ on: 'Change', action: permissionHandler }]
		},
		{
			displayableName: 'Administrador',
			valueName: 'is_staff',
			type: 'boolean',
			defaultValue: user?.is_staff,
			events: [{ on: 'Change', action: checkHandler }]
		},
		{
			displayableName: 'Fecha de Desactivación',
			valueName: 'deleted_at',
			type: 'text',
			readOnly: true,
			hidden: create,
			defaultValue: user?.deleted_at,
			events: [{ on: 'Change', action: checkHandler }]
		},
	]

	const customOnClose = () => {
		setError(null)
		onClose()
		if (modified) { viewReloader(Math.random()) }
	}

	const submitAction = async () => {
		
		if (modified) {
			let method = null
			if (create) {
				method = ApiCreateUser(requestBody)
			} else {
				method = ApiUpdateUser(user.id, requestBody)
			}
			method.then((_) => {
				customOnClose()
			}).catch((err) => {
				let data: { [x: string]: string } = err.response.data
				let fields: { field: string, description: string }[] = []
				for (const key in data) {
					fields.push({
						field: key,
						description: typeof data[key] !== 'string' ? data[key][0] : data[key]
					})
				}
				fields = fields.map((f) => {
					let _f = formFields.filter((ff) => ff.valueName === f.field)
					return _f[0] && typeof f.description === 'string' ? { field: _f[0].displayableName, description: f.description } : null
				}).filter((r) => r !== null)
				fields.length > 0 ? setError({
					message: "Revise los siguientes campos:",
					fieldErrors: fields
				}) : setError({ message: "Ocurrió un error inesperado." })

			})
		} else { customOnClose() }

	}

	return (
		<>
			<DynamicFormModal
				fields={formFields}
				isOpen={isOpen}
				onClose={customOnClose}
				title={title}
				buttonText={"Enviar"}
				buttonOnClick={submitAction}
				error={error}
			/>
		</>

	)
}

export default function UserTable(props: { data: User[], options: DynamicModalFormOptions[], reloader: (key: number) => void }) {

	const { data, options, reloader } = props;

	const columns: ColumnDef<User, any>[] = [
		helper.accessor('username', {
			id: 'username',
			header: () => <Header text="Usuario" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor((row) => `${row.first_name} ${row.last_name}`.trimEnd(), {
			id: 'full_name',
			header: () => <Header text="Nombre Completo" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor('department', {
			id: 'department',
			header: () => <Header text="Departamento" />,
			cell: (info: any) => <TextValue val={info.getValue().name} />
		}),
		helper.accessor('email', {
			id: 'email',
			header: () => <Header text="Correo" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor('last_login', {
			id: 'last_login',
			header: () => <Header text="Último Login" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor((row) => { return { id: row.id, is_active: row.is_active } }, {
			id: 'is_active',
			header: () => <Header text="Activo" />,
			cell: (info: any) => <CheckValue val={info.getValue()} methodUncheck={ApiDeleteUser} methodCheck={ApiEnableUser} reloader={reloader}/>
		}),
		helper.accessor((row) => row, {
			id: 'edit',
			header: () => <Header text="Editar" />,
			cell: (info: any) => {
				let value = info.getValue()
				return <EditModal
					modalComponent={UserManagementModal}
					modalProps={{
						object: value,
						mode: 'update',
						options: options,
						viewReloader: reloader
					}}
					disabled={false}
				/>
			}
		})
	];

	return (<ManagementTable data={data} columns={columns} />)
}